import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const PREF_LABELS = {
  inactivity_reminders: {
    label: 'Inactivity Reminders',
    description: 'Get nudged when you haven\'t logged in for a while',
  },
  milestone_notifications: {
    label: 'Milestone Notifications',
    description: 'Celebrate module completions, track completions, and level-ups',
  },
  weekly_digest: {
    label: 'Weekly Digest',
    description: 'A summary of your learning progress each week',
  },
};

export default function EmailPreferencesPage() {
  const { learner } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await api.get('/email/preferences');
      setPrefs(res.data);
    } catch (err) {
      setMessage('Failed to load email preferences.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleToggle = (key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/email/preferences', {
        inactivity_reminders: prefs.inactivity_reminders,
        milestone_notifications: prefs.milestone_notifications,
        weekly_digest: prefs.weekly_digest,
        unsubscribe_all: prefs.unsubscribe_all,
      });
      setPrefs(res.data);
      setMessage('Preferences saved successfully.');
    } catch (err) {
      setMessage('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all emails?')) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/email/preferences', { unsubscribe_all: true });
      setPrefs(res.data);
      setMessage('You have been unsubscribed from all emails.');
    } catch (err) {
      setMessage('Failed to unsubscribe. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.loadingText}>Loading preferences...</p>
        </div>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.errorText}>Unable to load email preferences.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Email Preferences</h1>
        <p style={styles.subtitle}>
          Manage which emails you receive from TeachIQ.
        </p>

        {prefs.email && (
          <div style={styles.emailDisplay}>
            <span style={styles.emailLabel}>Email address:</span>
            <span style={styles.emailValue}>{prefs.email}</span>
          </div>
        )}

        {prefs.unsubscribe_all && (
          <div style={styles.warningBanner}>
            You are currently unsubscribed from all emails. Toggle preferences below and save to re-enable.
          </div>
        )}

        <div style={styles.prefsSection}>
          {Object.entries(PREF_LABELS).map(([key, { label, description }]) => (
            <div key={key} style={styles.prefRow}>
              <div style={styles.prefInfo}>
                <span style={styles.prefLabel}>{label}</span>
                <span style={styles.prefDescription}>{description}</span>
              </div>
              <label style={styles.toggle}>
                <input
                  type="checkbox"
                  checked={!!prefs[key]}
                  onChange={() => handleToggle(key)}
                  disabled={saving}
                  style={styles.toggleInput}
                />
                <span style={{
                  ...styles.toggleSlider,
                  backgroundColor: prefs[key] ? 'var(--color-primary, #4F46E5)' : '#D1D5DB',
                }} />
              </label>
            </div>
          ))}
        </div>

        {message && (
          <p style={{
            ...styles.message,
            color: message.includes('success') || message.includes('unsubscribed')
              ? '#059669' : '#DC2626',
          }}>
            {message}
          </p>
        )}

        <div style={styles.actions}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={styles.saveButton}
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
          <button
            onClick={handleUnsubscribeAll}
            disabled={saving || prefs.unsubscribe_all}
            style={styles.unsubButton}
          >
            Unsubscribe from All
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#F3F4F6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  card: {
    width: '100%',
    maxWidth: '560px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    margin: '0 0 8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px',
  },
  emailDisplay: {
    padding: '12px 16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  emailLabel: {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: '500',
  },
  emailValue: {
    fontSize: '14px',
    color: '#1F2937',
    fontWeight: '600',
  },
  warningBanner: {
    padding: '12px 16px',
    backgroundColor: '#FEF3C7',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    color: '#92400E',
  },
  prefsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  prefRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
  },
  prefInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    marginRight: '16px',
  },
  prefLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1F2937',
  },
  prefDescription: {
    fontSize: '13px',
    color: '#6B7280',
  },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '26px',
    flexShrink: 0,
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
    position: 'absolute',
  },
  toggleSlider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '26px',
    transition: 'background-color 0.2s',
  },
  message: {
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  saveButton: {
    flex: 1,
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFFFFF',
    backgroundColor: 'var(--color-primary, #4F46E5)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    minWidth: '140px',
  },
  unsubButton: {
    flex: 1,
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#DC2626',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    cursor: 'pointer',
    minWidth: '140px',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '15px',
  },
  errorText: {
    textAlign: 'center',
    color: '#DC2626',
    fontSize: '15px',
  },
};
